import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
  untracked,
} from '@angular/core';
import { BaseFormControl, BaseSignalInput, SignalNumberField } from '@vallift/ngl';

export interface NumberGridColumn {
  id: string;
  label: string;
}

export interface NumberGridItem {
  id?: string;
  uniqueKey?: string;
  display: string;
}

export interface NumberGridColumnValue {
  id: string;
  value: number | null;
}

export interface NumberGridValue {
  id?: string;
  uniqueKey?: string;
  display: string;
  values: NumberGridColumnValue[];
}

/** Prefer id; accept legacy `key` when hydrating older configs/answers. */
export function readNumberGridColumnId(
  column: { id?: string; key?: string } | null | undefined,
): string {
  if (!column) {
    return '';
  }
  if (typeof column.id === 'string' && column.id.trim()) {
    return column.id.trim();
  }
  if (typeof column.key === 'string' && column.key.trim()) {
    return column.key.trim();
  }
  return '';
}

function readValueEntryId(
  entry: { id?: string; key?: string } | null | undefined,
): string {
  return readNumberGridColumnId(entry);
}

@Component({
  selector: 'lib-number-grid',
  standalone: true,
  imports: [SignalNumberField, BaseSignalInput],
  templateUrl: './number-grid.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberGridComponent extends BaseFormControl<NumberGridValue[]> {
  readonly label = input('Number Grid');
  readonly helpText = input('');
  readonly items = input.required<NumberGridItem[]>();
  readonly columns = input.required<NumberGridColumn[]>();

  readonly rows = computed<NumberGridValue[]>(() => this.value() ?? []);
  readonly previewMode = input<'desktop' | 'mobile'>('desktop');

  readonly isMobileView = computed(() => this.previewMode() === 'mobile');

  readonly expandedRowKeys = signal<string[]>([]);

  isExpanded(row: NumberGridValue): boolean {
    return this.expandedRowKeys().includes(this.rowKey(row));
  }

  toggleRow(row: NumberGridValue): void {
    if (this.isInteractionBlocked()) return;

    const key = this.rowKey(row);

    this.expandedRowKeys.update((keys) =>
      keys.includes(key)
        ? keys.filter((item) => item !== key)
        : [...keys, key],
    );
  }

  readonly isInteractionBlocked = computed(
    () => this.disabled() || this.readonly(),
  );

  constructor() {
    super();

    effect(() => {
      const items = this.items();
      const columns = this.columns();

      const currentValue = untracked(() => this.value());

      const currentRows: NumberGridValue[] = Array.isArray(currentValue)
        ? currentValue
        : [];

      const nextRows: NumberGridValue[] = items.map((item) => {
        const existing = currentRows.find((row) =>
          item.id !== undefined
            ? row.id === item.id
            : item.uniqueKey !== undefined
              ? row.uniqueKey === item.uniqueKey
              : row.display === item.display,
        );

        return {
          ...item,
          values: columns.map((column) => {
            const columnId = readNumberGridColumnId(column);
            const existingValue = existing?.values?.find(
              (entry) => readValueEntryId(entry) === columnId,
            );

            return {
              id: columnId,
              value: existingValue?.value ?? null,
            };
          }),
        };
      });

      const unchanged =
        currentRows.length === nextRows.length &&
        nextRows.every((row, index) => {
          const current = currentRows[index];

          return (
            current.id === row.id &&
            current.uniqueKey === row.uniqueKey &&
            current.display === row.display &&
            current.values?.length === row.values.length &&
            row.values.every(
              (entry, valueIndex) =>
                readValueEntryId(current.values?.[valueIndex]) === entry.id &&
                current.values?.[valueIndex]?.value === entry.value,
            )
          );
        });

      if (!unchanged) {
        this.value.set(nextRows);
      }
    });
  }

  rowKey(row: NumberGridValue): string {
    if (row.id !== undefined) {
      return `id:${row.id}`;
    }

    if (row.uniqueKey !== undefined) {
      return `unique:${row.uniqueKey}`;
    }

    return `display:${row.display}`;
  }

  getValue(row: NumberGridValue, columnId: string): number | null {
    return (
      row.values.find((entry) => readValueEntryId(entry) === columnId)
        ?.value ?? null
    );
  }

  onValueChange(
    rowIndex: number,
    columnId: string,
    value: number | null,
  ): void {
    if (this.isInteractionBlocked()) {
      return;
    }

    const currentRows = this.value() ?? [];

    const nextRows = currentRows.map((row, index) => {
      if (index !== rowIndex) {
        return row;
      }

      return {
        ...row,
        values: row.values.map((entry) =>
          readValueEntryId(entry) === columnId ? { ...entry, id: columnId, value } : entry,
        ),
      };
    });

    this.value.set(nextRows);
  }

  validate(): boolean {
    if (!this.required()) {
      return true;
    }

    const rows = this.value() ?? [];

    return (
      rows.length > 0 &&
      rows.every((row) => row.values.every((entry) => entry.value !== null))
    );
  }
}
